/**
 * The /channels reads (spec §2.1, §2.2): get_channel_facts, get_channel_entities
 * and get_channel_ads for a scope and ONE channel. Each is paged, because
 * PostgREST answers an unpaged read with at most 1000 rows, HTTP 200 and no
 * error. Each is ordered by a total key (facts: date, ad_set_id; entities:
 * campaign_id, ad_set_id; ads: ad_id), so pages never overlap or skip. Every
 * reader THROWS on a failed page: a partial read would show as smaller totals
 * nobody measured. Runs on the browser client, under the caller RLS.
 *
 * @module services/channels/channelReaders
 */
import { getSupabaseClient } from '@/config';
import { errorMessageOf, readAllRows, type Page } from '@/services/supabasePaging';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { nullableNumber } from '@/services/overview/overviewMath';
import type { ChannelAdRow, ChannelEntity, ChannelFactRow, ChannelPlatform, EntityStatus } from './channelTypes';

/** A get_channel_facts row as PostgREST returns it: numeric may arrive as a string. */
export interface ChannelFactDb {
  date: string;
  workspace_id: string;
  ad_set_id: string;
  campaign_id: string;
  objective: string;
  currency: string | null;
  spend: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  conversions: number | string | null;
  conversion_value: number | string | null;
  leads: number | string | null;
  video_views: number | string | null;
  engagements: number | string | null;
  reactions: number | string | null;
  comments: number | string | null;
  shares: number | string | null;
  link_clicks: number | string | null;
  sessions: number | string | null;
}

/** A get_channel_entities row: one ad set with its campaign and account. */
export interface ChannelEntityDb {
  workspace_id: string;
  account_id: string | null;
  account_name: string | null;
  account_external_id: string | null;
  currency: string | null;
  campaign_id: string;
  campaign_name: string | null;
  campaign_external_id: string | null;
  campaign_status: string | null;
  campaign_start_date: string | null;
  campaign_end_date: string | null;
  objective: string;
  ad_set_id: string;
  ad_set_name: string | null;
  ad_set_external_id: string | null;
  ad_set_status: string | null;
  last_spend_date: string | null;
}

/** A get_channel_ads row: one ad and its sums over the window; numeric may arrive as a string. */
export interface ChannelAdDb {
  workspace_id: string;
  ad_id: string;
  ad_set_id: string;
  campaign_id: string;
  objective: string;
  currency: string | null;
  ad_name: string | null;
  ad_format: string | null;
  ad_status: string | null;
  thumbnail_url: string | null;
  external_ad_id: string | null;
  spend: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  reactions: number | string | null;
  comments: number | string | null;
  shares: number | string | null;
}

const ENTITY_STATUSES: readonly EntityStatus[] = ['active', 'paused', 'completed', 'archived'];

/** Padding is no part of a value, and a blank value is none. Shared with /creatives' reader (D3 R21). */
export const trimmedOrNull = (v: string | null): string | null => (v ? v.trim() || null : null);

/** As D1 factOf: padding after the code would make a second currency, and a blank currency is unknown. */
const currencyOf = trimmedOrNull;

/**
 * A platform's native id, normalised once, here (M3): padding would reach an Ads Manager URL as %20, and a
 * blank id would draw an empty ID line, its copy button and ↗. Every consumer then needs only a null check.
 */
const nativeIdOf = trimmedOrNull;

/** A blank text cell is no value: most local media_ads rows store ad_format as an empty string. Shared with /creatives (D3 R21). */
export const textOrNull = (v: string | null): string | null => (v !== null && v.trim() !== '' ? v : null);

/** The campaign_status enum as stored; anything else is unknown (null), never Active. */
export const entityStatusOf = (v: string | null): EntityStatus | null =>
  ENTITY_STATUSES.find((s) => s === v) ?? null;

/**
 * R4: a FactRow, so D1 math serves D2 unchanged. The RPC has no platform, account
 * or reach: the platform is the one the reader asked for, account and reach are null.
 */
export const channelFactOf = (r: ChannelFactDb, platform: ChannelPlatform): ChannelFactRow => ({
  date: r.date,
  workspaceId: r.workspace_id,
  platform,
  objective: r.objective,
  accountId: null,
  accountName: null,
  currency: currencyOf(r.currency),
  spend: nullableNumber(r.spend),
  impressions: nullableNumber(r.impressions),
  clicks: nullableNumber(r.clicks),
  conversions: nullableNumber(r.conversions),
  conversionValue: nullableNumber(r.conversion_value),
  reach: null,
  adSetId: r.ad_set_id,
  campaignId: r.campaign_id,
  leads: nullableNumber(r.leads),
  videoViews: nullableNumber(r.video_views),
  engagements: nullableNumber(r.engagements),
  reactions: nullableNumber(r.reactions),
  comments: nullableNumber(r.comments),
  shares: nullableNumber(r.shares),
  linkClicks: nullableNumber(r.link_clicks),
  sessions: nullableNumber(r.sessions),
});

export const channelEntityOf = (r: ChannelEntityDb): ChannelEntity => ({
  workspaceId: r.workspace_id,
  accountId: r.account_id,
  accountName: r.account_name,
  accountExternalId: nativeIdOf(r.account_external_id),
  currency: currencyOf(r.currency),
  campaignId: r.campaign_id,
  campaignName: r.campaign_name,
  campaignExternalId: nativeIdOf(r.campaign_external_id),
  campaignStatus: entityStatusOf(r.campaign_status),
  campaignStartDate: r.campaign_start_date,
  campaignEndDate: r.campaign_end_date,
  objective: r.objective,
  adSetId: r.ad_set_id,
  adSetName: r.ad_set_name,
  adSetExternalId: nativeIdOf(r.ad_set_external_id),
  adSetStatus: entityStatusOf(r.ad_set_status),
  lastSpendDate: r.last_spend_date,
});

export const channelAdOf = (r: ChannelAdDb): ChannelAdRow => ({
  workspaceId: r.workspace_id,
  adId: r.ad_id,
  adSetId: r.ad_set_id,
  campaignId: r.campaign_id,
  objective: r.objective,
  currency: currencyOf(r.currency),
  adName: r.ad_name,
  adFormat: textOrNull(r.ad_format),
  adStatus: entityStatusOf(r.ad_status),
  thumbnailUrl: r.thumbnail_url,
  externalAdId: nativeIdOf(r.external_ad_id),
  spend: nullableNumber(r.spend),
  impressions: nullableNumber(r.impressions),
  clicks: nullableNumber(r.clicks),
  reactions: nullableNumber(r.reactions),
  comments: nullableNumber(r.comments),
  shares: nullableNumber(r.shares),
});

type PageOf<T> = (from: number, to: number) => PromiseLike<Page<T>>;

/** Every page, or a throw: never a partial list. */
const readOrThrow = async <T>(what: 'facts' | 'entities' | 'ads', page: PageOf<T>): Promise<T[]> => {
  const { rows, error } = await readAllRows<T>(page, `channel ${what} read`);
  if (error) throw new Error(`channel ${what} read failed: ${errorMessageOf(error)}`);
  return rows;
};

/** One row per day and ad set (R1: read twice, the period and the one before it). */
export const readChannelFacts = async (
  workspaceIds: readonly string[],
  platform: ChannelPlatform,
  range: DateRange,
): Promise<ChannelFactRow[]> => {
  if (workspaceIds.length === 0) return [];
  const rows = await readOrThrow<ChannelFactDb>('facts', (from, to) =>
    getSupabaseClient()
      .rpc('get_channel_facts', { p_workspace_ids: [...workspaceIds], p_platform: platform, p_start: range.start, p_end: range.end })
      .order('date', { ascending: true })
      .order('ad_set_id', { ascending: true })
      .range(from, to));
  return rows.map((r) => channelFactOf(r, platform));
};

/** One row per ad set of the channel; not windowed (R1). */
export const readChannelEntities = async (
  workspaceIds: readonly string[],
  platform: ChannelPlatform,
): Promise<ChannelEntity[]> => {
  if (workspaceIds.length === 0) return [];
  const rows = await readOrThrow<ChannelEntityDb>('entities', (from, to) =>
    getSupabaseClient()
      .rpc('get_channel_entities', { p_workspace_ids: [...workspaceIds], p_platform: platform })
      .order('campaign_id', { ascending: true })
      .order('ad_set_id', { ascending: true })
      .range(from, to));
  return rows.map(channelEntityOf);
};

/** One row per ad with metrics in the window (R1: the current window only). */
export const readChannelAds = async (
  workspaceIds: readonly string[],
  platform: ChannelPlatform,
  range: DateRange,
): Promise<ChannelAdRow[]> => {
  if (workspaceIds.length === 0) return [];
  const rows = await readOrThrow<ChannelAdDb>('ads', (from, to) =>
    getSupabaseClient()
      .rpc('get_channel_ads', { p_workspace_ids: [...workspaceIds], p_platform: platform, p_start: range.start, p_end: range.end })
      .order('ad_id', { ascending: true })
      .range(from, to));
  return rows.map(channelAdOf);
};
